import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

let API_URL = "";
if (process.env.REACT_APP_Environment === 'development')
  API_URL = process.env.REACT_APP_API_URL;
else
  API_URL = process.env.REACT_APP_API_URL_Production;

const API_BASE_URL = String(API_URL || "").trim().replace(/\/?$/, "/");

export const getApiBaseUrl = () => API_BASE_URL;

const secureStorage = {
  setToken: (token) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('token_flag', 'true');
  },

  getToken: () => localStorage.getItem('auth_token'),

  setRefreshToken: (token) => {
    localStorage.setItem('refresh_token', token);
  },

  getRefreshToken: () => localStorage.getItem('refresh_token'),

  setUserData: (userData) => {
    localStorage.setItem('user_data', JSON.stringify(userData));
  },

  getUserData: () => {
    const data = localStorage.getItem('user_data');
    return data ? JSON.parse(data) : null;
  },

  clearAll: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token_flag');
    localStorage.removeItem('isExpired');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('auth_token');
    return token !== null && token !== '';
  },

  migrateFromLocalStorage: () => {
    const oldToken = localStorage.getItem('token');
    const oldUser = localStorage.getItem('user');
    const oldRefresh = localStorage.getItem('refreshToken');

    if (oldToken) {
      secureStorage.setToken(oldToken);
      localStorage.removeItem('token');
    }
    if (oldUser) {
      try {
        const parsed = JSON.parse(oldUser);
        secureStorage.setUserData({ ...parsed, isSuperUser: true });
      } catch (_) {
        /* ignore */
      }
      localStorage.removeItem('user');
    }
    if (oldRefresh) {
      secureStorage.setRefreshToken(oldRefresh);
      localStorage.removeItem('refreshToken');
    }
  },
};

if (typeof window !== 'undefined') {
  secureStorage.migrateFromLocalStorage();
}

export function getUserDetails() {
  try {
    const userData = secureStorage.getUserData();
    if (userData) return userData;

    const userString = localStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      secureStorage.setUserData({ ...user, isSuperUser: true });
      localStorage.removeItem('user');
      return { ...user, isSuperUser: true };
    }
    return {};
  } catch (_) {
    return {};
  }
}

export function isSuperUser(u) {
  const d = u || getUserDetails();
  if (d.isSuperUser) return true;
  try {
    const token = secureStorage.getToken();
    if (token) {
      const decoded = jwtDecode(token);
      return decoded.isSuperUser === true || decoded.isSuperUser === 1;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

/** Super users have no fisher subscription expiry. */
export async function CheckUserExpiry() {
  return null;
}

const axiosInstance = axios.create({
  baseURL: API_URL,
});

let refreshInFlight = null;
const TOKEN_REFRESH_BUFFER_SEC = 30 * 60;

const refreshAccessToken = async () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refreshToken = secureStorage.getRefreshToken();
    if (!refreshToken) {
      secureStorage.clearAll();
      window.location.href = '/user/login';
      return null;
    }
    try {
      const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const response = await axios.post(`${base}/api/User/RefreshToken`, { refreshToken });
      const newAccessToken = response.data?.token;
      if (!newAccessToken) throw new Error('No token in refresh response');
      secureStorage.setToken(newAccessToken);
      return newAccessToken;
    } catch (_) {
      secureStorage.clearAll();
      window.location.href = '/user/login';
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

const ensureFreshAccessToken = async () => {
  const token = secureStorage.getToken();
  if (!token) return null;
  const refreshToken = secureStorage.getRefreshToken();
  if (!refreshToken) return token;
  try {
    const decoded = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp - now > TOKEN_REFRESH_BUFFER_SEC) {
      return token;
    }
  } catch {
    /* refresh */
  }
  return refreshAccessToken();
};

axiosInstance.interceptors.request.use(async (config) => {
  const token = await ensureFreshAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function isJwtAuthFailure(error) {
  const msg = error?.response?.data?.message;
  return msg === 'Invalid or expired token' || msg === 'No token provided';
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (!isJwtAuthFailure(error)) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      const user = secureStorage.getUserData();
      if (isSuperUser(user)) {
        secureStorage.clearAll();
        window.location.href = '/user/login';
        return Promise.reject(error);
      }
      try {
        const refreshToken = secureStorage.getRefreshToken();
        if (!refreshToken) {
          secureStorage.clearAll();
          window.location.href = '/user/login';
          return Promise.reject(error);
        }
        const response = await axios.post(`${API_URL}/api/User/RefreshToken`, { refreshToken });
        const { token: newAccessToken } = response.data;
        secureStorage.setToken(newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        secureStorage.clearAll();
        window.location.href = '/user/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;

export const canManagePlatform = (u) => isSuperUser(u);

export const canWriteOperationalPonds = (u) => isSuperUser(u);

export const canEditSamplingFishData = (u) => isSuperUser(u);

export const canDeletePondDetail = (u) => isSuperUser(u);

export const hasScientistPondUIAccess = (u) => isSuperUser(u);

export const getStaffId = () => null;

export const isFisherWideUser = () => false;

export const isAnglerOnlyUser = () => false;

export const checkTokenValidity = async () => {
  const token = secureStorage.getToken();
  if (!token) return false;

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const secondsLeft = decodedToken.exp ? decodedToken.exp - currentTime : TOKEN_REFRESH_BUFFER_SEC + 1;
    if (secondsLeft <= TOKEN_REFRESH_BUFFER_SEC) {
      const newToken = await refreshAccessToken();
      return !!newToken;
    }
    return true;
  } catch (_) {
    const newToken = await refreshAccessToken();
    return !!newToken;
  }
};

export const setAuthTokens = (accessToken, refreshToken, userData) => {
  secureStorage.setToken(accessToken);
  if (refreshToken) {
    secureStorage.setRefreshToken(refreshToken);
  }
  if (userData) {
    let isSuperUserFlag = !!userData.isSuperUser;
    try {
      const decoded = jwtDecode(accessToken);
      if (decoded.isSuperUser === true || decoded.isSuperUser === 1) {
        isSuperUserFlag = true;
      }
    } catch (_) {
      /* ignore */
    }
    secureStorage.setUserData({ ...userData, isSuperUser: isSuperUserFlag });
  }
};

export const clearAuthData = () => {
  secureStorage.clearAll();
};

export const isAuthenticated = () => secureStorage.isAuthenticated();
